/**
 * Cross-app view-wiring contract.
 *
 * Single source of truth for the string keys that cross the raft-SSR ↔
 * web-client seam:
 *
 *  - `VIEW_ACTIONS`: every action name the raft views may emit via
 *    `data-fn` / `data-on-*` / `data-init-fn` attributes (resolved by the
 *    web-client actionRegistry). Web-client registers many more actions for
 *    internal use — only the ones referenced from SSR markup belong here.
 *  - `MODAL_KEYS`: every modal slug web-client's modalRegistry can render.
 *    Views open modals via `data-modal="<key>:<parts>"` / `modal("<key>:…")`.
 *
 * IMPORTANT: this module must stay dependency-free (no Zod) — the client
 * bundle value-imports it via the `./viewContract` subpath export.
 */
export const VIEW_ACTIONS = [
    "accountreturn",
    "admmarkpaid",
    "apiDocsLang",
    "briefdeliverred",
    "briefinprogress",
    "checkitopsearch",
    "clearQd",
    "clearfilters",
    "dpifilter",
    "close",
    "closemodallast",
    "dashboardSummaryMirror",
    "dashboardpayoutfilter",
    "dashboardsearch",
    // deselectall / multi / removeFromCollection are dispatched by Chrome.tsx's
    // select-popup. They were missing here for as long as that markup existed,
    // because it passes attribute *bags* as props — a form no regex checker sees
    // and which never reached `ViewActionName`.
    "deselectall",
    "dofollow",
    "dounfollow",
    "editnotes",
    // The reseller /expired delete list: row selection and the selected-only confirm.
    "expiredConfirmSelected",
    "expiredSelectAll",
    "expiredSelectNone",
    "expiredToggle",
    "followSearch",
    "hdpreviewclose",
    "idesel",
    "ieclientDeleteFile",
    "ieclientrefresh",
    "inext",
    "initIeClientUpload",
    "interiorCardClick",
    "interiorselect",
    "metadataStart",
    "minimisetransfers",
    "multi",
    "multiAddDownloadcart",
    "newfeatureclose",
    "onlygroup",
    "onlygroupartist",
    "onlymine",
    "onlymineartist",
    "partnerSelectAll",
    "partnerselect",
    "prestatus",
    "qmsg",
    "refreshartistdashboard",
    "removeFromCollection",
    "reorder",
    "reupload",
    "scrollto",
    "searchtimed",
    "searchtyped",
    "searchmodechanged",
    "directorysearchtyped",
    "selectToggle",
    "selectall",
    "showtransfers",
    "sidebar",
    "sidebarShowLess",
    "sidebarShowMore",
    "toclipboard",
    "toggleDownloadCart",
    "toggleInteriorSel",
    "toggleOption",
    "togglePartner",
    "togglemobilemenu",
    "transferexpand",
    "unfoldBio",
    "view",
];
export const MODAL_KEYS = [
    "addadvance",
    "artistapplication",
    "downloadernotice",
    "invoicereminders",
    "addcollection",
    "addfeaturedartist",
    "addgroup",
    "addgroupartist",
    "addkeywordinterior",
    "addlist",
    "addplugin",
    "addproduct",
    "addproduct_images",
    "addtoartistgroups",
    "addtocollection",
    "addtopartnergroups",
    "adduser",
    "addvariant",
    "admin",
    "alternative",
    "artistdetails",
    "artistfeatures",
    "artistgroups",
    "artworkcollections",
    "biography",
    "biography-article",
    "biography-create",
    "blockuserfrompartners",
    "blockwords",
    "blockwords-ai",
    "briefcollection",
    "briefs",
    "bulksetvalues",
    "changepassword",
    "collectiondetails",
    "collectionthumb",
    "collwizard",
    "wizardresellers",
    "contact",
    "customizetransfer",
    "defaultinterior",
    "details",
    "directory",
    "directoryedit",
    "mockupsall",
    "download",
    "downloads",
    "duplicates",
    "earnings",
    "ecommerce",
    // TODO(restore-modal): opened from List/Collection/Artist views + multi-select
    // transfer, but no client component exists yet (renders "Unknown modal") —
    // see todo/broken-modals-restoration.md.
    "ecommercesync",
    "editbrief",
    "editcollcat",
    "editcollection",
    "editinterior",
    "editinterioruser",
    "editlist",
    "editnewsletter",
    "editnewsletter-artist",
    "editpage",
    "editphoto",
    "editvariant",
    "expanded-products",
    "expanded-products-no",
    "expanded-products-select",
    "expanded-products-yes",
    "expconfirm",
    "expnotice",
    "expviewer",
    "featureartist",
    "firstquestions",
    "forgotpassword",
    "ieclient",
    "ieclient-select",
    "image-request-sent",
    "incomingartworkchecker",
    "invoices",
    "linkcollection",
    "login",
    "managefollowed",
    "marketing",
    // Marketing material as a RESELLER sees it, on an artist's profile (read-only, download).
    // `marketing` is the artist's own manager and `mats` the admin view of the same files.
    "marketingmaterial",
    "mats",
    "metadatascreening",
    "metric",
    "mockup-generate",
    "mockup-select",
    "mockupwelcome",
    "multiaddtag",
    "multicollection",
    "multideletetag",
    "multilist",
    "multimeta",
    "newcollcat",
    "newsletters",
    "newslettersend",
    "newversion",
    "notforpictufy",
    "pages",
    "partnercollection",
    "partnerdetails",
    "manageinventory",
    "importinventory",
    "partnergroups",
    "portfolioreviewguide",
    "pre_view",
    "previewinterior",
    "previewinterior2",
    "pricing",
    "profilepic",
    "reorderinteriors",
    "reorderlist",
    "resetpassword",
    "resetpasswordsuccess",
    "edituser",
    "resetpw",
    "reupload",
    "savesearch",
    "selectartistsforgroup",
    "selectinterior",
    "selectinterior2",
    "selectlang",
    "selectpartnersforgroup",
    "selectrecipients",
    "selectrecipients-artist",
    "selectresellers",
    "sendnewsletter",
    "sendnewsletter-artist",
    "setcustomdl",
    "setthumb",
    "settings",
    "showinterior",
    "signup",
    "signup-artist",
    "uploadinterioruser",
    "uploadrules",
    "view",
    "withselected",
    "yourphotos",
];
//# sourceMappingURL=viewContract.js.map